import type { AppProps } from 'next/app'
import NextNprogress from 'nextjs-progressbar'
import axios from 'axios'
import Head from 'next/head'

import { useRouter } from 'next/router'
//import auth from '@/utils/services/auth'
import { useAuth } from '@/hooks/useAuth'
import { useEffect, useState } from 'react'
import Toast from '@/components/molecules/Toast'

// Store
import { wrapperStore } from '@/store'

// Styles
import { globalStyles, colors } from '@/theme'

import { createTheme, ThemeProvider } from '@material-ui/core/styles'

import nookies from 'nookies'
import ErrorBoundaryFallback from '@/components/organisms/ErrorBoundaryFallback'

import { ErrorBoundary } from 'react-error-boundary'

import AppContext from '../AppContext'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { DealerDetailsProvider } from '@/context/dealerDetailsContext'
import { DealerDetailsUpdate } from '@/components/molecules/ModalDealerDetails'
import { useSelector } from 'react-redux'
import { TStoreState } from '@/types/store/state'

interface MyAppProps extends AppProps {
  Component: any
  pageProps: {
    user: any
    jwt: any
  }
}

function MyApp({ Component, pageProps }: MyAppProps) {

  const isDealerUpdatingAddr=useSelector((state:TStoreState)=>state.isUpdatingDealerAddress.isUpdatingDealerAddress)
  const [shouldShowPopup,setShouldShowPopup]=useState(false)

  const theme = createTheme({
    typography: {
      fontFamily: 'Inter, sans-serif',
    },
    palette: {
      primary: {
        main: colors.primary,
      },
      secondary: {
        main: colors.secondary,
      },
    },
  })

  const router = useRouter()
  const { setSession,session } = useAuth()
  const [openToast, setOpenToast] = useState(false)
  const [responseAlert, setResponseAlert] = useState({
    severity: '',
    message: '',
  })

  const [permissions, setPermissions] = useState(null)

  axios.interceptors.request.use(
    (req) => {
      const token = nookies.get(null, 'session')['session']
      if (token) {
        req.headers['Authorization'] = 'Bearer ' + token
      }
      return req
    },
    function (error) {
      return Promise.reject(error)
    }
  )

  axios.interceptors.response.use(
    (response) => {
      return response
    },
    (error) => {
      const status = error?.response?.status

      switch (status) {
        case 401:
          router.replace('/auth/login')
          break
        case 403:
          router.replace('/error/403')
          break
        case 500:
          setResponseAlert({
            severity: 'error',
            message: 'Looks like the server is taking too long to respond, please try again later',
          })
          setOpenToast(true)
          break
      }

      return Promise.reject(error)
    }
  )

  if (pageProps && pageProps.user && pageProps.jwt) setSession(pageProps.user, pageProps.jwt)

  const breakpoints = {
    isDesktop: useMediaQuery('(min-width: 960px)'),
    isTablet: useMediaQuery('(max-width: 959px) and (min-width: 710px)'),
    isPhablet: useMediaQuery('(max-width: 709px) and (min-width: 600px)'),
    isMobile: useMediaQuery('(max-width: 709px)'),
  }

  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  useEffect(()=>{
     setShouldShowPopup((session?.auth?.jwt) && (!window.location.href.includes('login'))); 
  },[session])

  return (
    <>
      <style jsx global>
        {globalStyles}
      </style>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      </Head>
      <ThemeProvider theme={theme}>
        <NextNprogress
          color={colors.primary}
          startPosition={0.3}
          stopDelayMs={200}
          height={3}
          options={{
            showSpinner: false,
          }}
        />
        <ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onReset={() => router.back()}>
          <AppContext.Provider
            value={{
              sidebarState: { value: isSidebarOpen, set: setIsSidebarOpen },
              state: permissions,
              breakpoints: breakpoints,
              setPermissions: setPermissions,
              fireToast: () => setOpenToast(true),
              setToast: (severity, message) => {
                setResponseAlert({ severity, message })
              },
            }}
          >
            <Component {...pageProps} />
             
            {shouldShowPopup && <DealerDetailsProvider>
          <DealerDetailsUpdate open={isDealerUpdatingAddr}/>
          </DealerDetailsProvider>}
          </AppContext.Provider>
        </ErrorBoundary>
        <Toast
          open={openToast}
          handleClose={() => setOpenToast(false)}
          severity={responseAlert.severity}
          message={responseAlert.message}
        />
      </ThemeProvider>
    </>
  )
}

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// MyApp.getInitialProps = async (appContext: AppContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);

//   return { ...appProps }
// }

export default wrapperStore.withRedux(MyApp)
